<script setup>
import { searchgetQueryParamsFromUrl } from "@/utils/api-drive-params";
import productListUtils from "@/utils/product-list";

import { loadPageWithLoader } from "@/utils/webpack-helper";
import { useProductSearchStore } from "@/store/product-search";
import { useContextStore } from "@/store/context";
import { useMagasinStore } from "@/store/magasin";
import { useClientStore } from "@/store/client";
import { useContainerProductList } from "@/containers/mixin/product-list.mixin";
import { useContainerPushAbonnementLad } from "@/containers/mixin/push-abonnement-lad-mixin";
import { useAutoSuggestStore } from "@/store/auto-suggest";
import { useTrackingRecherche } from "@/mixins/tracking/page-recherche";

const route = useRoute();
const app = useNuxtApp();

const { trackRecherche, emitEventPartenaires } = useTrackingRecherche();

const { updateLoadingStatus } = useProductSearchStore();
const { setKeywords } = useAutoSuggestStore();
const { id: magasinId } = storeToRefs(useMagasinStore());
const { isPro } = storeToRefs(useClientStore());
const { fetchFiltredSearchResults } = useProductSearchStore();
const { showAboLad, showAboLadRenouvellement } =
  useContainerPushAbonnementLad();
const {
  getAxiosErrorFromAsyncData,
  getErrorStatus,
  getErrorMessage,
  isPageIndexError
} = useAxiosHelper();
const {
  isLoading,
  sortCriteria,
  appliedFilters,
  productsCount,
  pushZones,
  seoHead,
  productsList,
  redirection
} = storeToRefs(useProductSearchStore());
const { layout, from } = storeToRefs(useContextStore());
const component = computed(() => {
  return loadPageWithLoader(
    lazyComponents[`/components/pages/product-search/${layout.value}.vue`]
  );
});

const { searchFetchFiltredProducts } = useContainerProductList(
  productsList.value,
  sortCriteria.value,
  appliedFilters.value
);

if (route.query?.keywords) {
  setKeywords(route.query.keywords);
}

const unsubPage = app.hook("page:finish", () => {
  updateLoadingStatus(false);
});

onBeforeUnmount(unsubPage);

definePageMeta({
  key: (route) => {
    const params = [];
    if (route.query && route.query.sortby) {
      params.push(route.query.sortby);
    }
    if (route.query && route.query.keywords) {
      params.push(route.query.keywords);
    }
    return route.path + params.join("-");
  }
});

onMounted(() => {
  if (app.payload.serverRendered && app.isHydrating && process.client) {
    // si render coté serveur, on scroll en haut de page
    window.scrollTo(0, 0);
  } else {
    // si on arrive d'une FP, on scroll sur la tuile de l'article correspondant dans la DP
    setTimeout(() => {
      if (from.value?.name === "article-id-slug" && from.value?.params?.id) {
        productListUtils.scrollToProductId(from.value.params.id);
      }
    }, 100);
  }

  trackRecherche();
});

const conf = useRuntimeConfig();

useHead(() => {
  return {
    ...seoHead.value,
    title: seoHead.value.title || `Recherche : ${route.query.keywords || ""}`,
    meta: [
      {
        name: "apple-itunes-app",
        content: `app-id=${conf.public.IOS_CORADRIVE_APP_STORE_ID}`
      }
    ]
  };
});

if (!route.query.keywords) {
  await navigateTo("/", { replace: true, redirectCode: 301 });
}

const { error } = await useAsyncData(() => {
  const queryParams = searchgetQueryParamsFromUrl(route.params, route.query);
  const parameters = {
    shopId: magasinId.value,
    isPro: isPro.value
  };
  return fetchFiltredSearchResults({
    ...parameters,
    ...queryParams
  });
});

const responseError = getAxiosErrorFromAsyncData(error);
if (responseError) {
  if (isPageIndexError(responseError)) {
    await navigateTo(
      { path: route.path, query: { keywords: route.query.keywords } },
      { replace: true, redirectCode: 301 }
    );
  } else {
    throw showError({
      statusCode: getErrorStatus(responseError),
      statusMessage: getErrorMessage(responseError)
    });
  }
}

if (redirection.value) {
  await navigateTo(redirection.value, { replace: true, redirectCode: 301 });
}

emitEventPartenaires();
</script>

<template>
  <div>
    <component
      :is="component"
      :is-loading="isLoading"
      :sort-criteria="sortCriteria"
      :push-zones="pushZones"
      :show-abo-lad="showAboLad"
      :show-abo-lad-renouvellement="showAboLadRenouvellement"
      @fetchFiltredProducts="searchFetchFiltredProducts"
    />
  </div>
</template>
